import type { MarketSeriesData } from '../../../api/marketPricesClient';
import { quoteWith, SOURCES } from '../../model/capabilities';
import type { Outcome } from '../../model/types';
import type { ChartSeries, SeriesData, SeriesPoint } from '../seriesTypes';
import { othersPoints } from '../stacked';

export type ProbabilityMode = 'line' | 'candles' | 'stacked';

export function probabilitySeries(
    outcomes: readonly Outcome[],
    data: readonly MarketSeriesData[],
    colors: ReadonlyMap<string, string>,
    mode: ProbabilityMode = 'line',
    showSource = false,
    othersColor = '#888888',
    othersLabel: string | null = null,
    liveAt: number | null = null
): ChartSeries[] {
    const byMarket = new Map(data.map(d => [d.conditionId, d.data]));
    const asCandles = mode === 'candles' && outcomes.length === 1;
    const asStack = mode === 'stacked';
    const result: ChartSeries[] = [];
    const stackedLists: SeriesPoint[][] = [];
    const latest = (outcome: Outcome): number => {
        const quote = quoteWith(outcome, 'probability');
        const candles = quote ? byMarket.get(quote.market.marketId) : undefined;
        return candles?.[candles.length - 1]?.close ?? quote?.probability ?? 0;
    };
    const ordered = asStack
        ? [...outcomes].sort((a, b) => latest(b) - latest(a))
        : outcomes;
    for (const outcome of ordered) {
        const quote = quoteWith(outcome, 'probability');
        if (!quote) continue;
        const candles = byMarket.get(quote.market.marketId) ?? [];
        const points = candles.map(c => ({ t: c.t.getTime(), v: c.close }));
        const lastT = points[points.length - 1]?.t ?? -Infinity;
        const live =
            !asCandles &&
            liveAt !== null &&
            quote.live === true &&
            quote.probability !== null &&
            liveAt > lastT;
        if (live && quote.probability !== null) {
            points.push({ t: liveAt, v: quote.probability });
        }
        const seriesData: SeriesData = asCandles
            ? {
                  kind: 'candles',
                  candles: candles.map(c => ({
                      t: c.t.getTime(),
                      open: c.open,
                      high: c.high,
                      low: c.low,
                      close: c.close,
                  })),
              }
            : { kind: 'points', points };
        result.push({
            id: `prob:${outcome.id}:${quote.source}`,
            label: showSource
                ? `${outcome.label} (${SOURCES[quote.source].label})`
                : outcome.label,
            color: colors.get(outcome.id) ?? '#888888',
            unit: 'probability',
            draw: asCandles ? 'candles' : asStack ? 'area' : 'line',
            axis: 'shared',
            home: 'probability',
            movable: false,
            hasData: candles.length > 0,
            ...(live ? { live: true } : {}),
            image: outcome.image,
            data: seriesData,
        });
        if (asStack && seriesData.kind === 'points') {
            stackedLists.push(seriesData.points);
        }
    }
    if (asStack && stackedLists.some(points => points.length > 0)) {
        result.push({
            id: 'prob:others',
            label: othersLabel ?? (outcomes.length === 1 ? 'NO' : 'Others'),
            color: othersColor,
            unit: 'probability',
            draw: 'area',
            axis: 'shared',
            home: 'probability',
            movable: false,
            hasData: true,
            faint: true,
            data: { kind: 'points', points: othersPoints(stackedLists) },
        });
    }
    return result;
}
